/**
 * Assessment Update Notifier
 * Provides a lightweight event system to notify subscribers about assessment updates
 * without relying on external libraries or complex state management.
 */

type AssessmentUpdateListener = (assessmentType: string) => void;

class AssessmentUpdateNotifier {
  private listeners: Set<AssessmentUpdateListener> = new Set();
  private lastUpdateTime: number = 0;
  private debounceMs: number = 500; // Prevent multiple rapid updates

  /**
   * Subscribe to assessment updates
   */
  subscribe(listener: AssessmentUpdateListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all subscribers about an assessment update
   * Automatically debounces rapid calls within 500ms
   */
  notifyUpdate(assessmentType: string): void {
    const now = Date.now();
    
    // Debounce: ignore updates that come too quickly
    if (now - this.lastUpdateTime < this.debounceMs) {
      return;
    }

    this.lastUpdateTime = now;
    
    // Notify all listeners asynchronously to prevent blocking
    // Use setTimeout to ensure this doesn't interfere with assessment submission
    setTimeout(() => {
      this.listeners.forEach(listener => {
        try {
          listener(assessmentType);
        } catch (error) {

        }
      });
    }, 0);
  }

  /**
   * Clear all listeners
   */
  clearListeners(): void {
    this.listeners.clear();
  }

  /**
   * Get current number of listeners (for debugging)
   */
  getListenerCount(): number {
    return this.listeners.size;
  }
}

// Export singleton instance
export const assessmentUpdateNotifier = new AssessmentUpdateNotifier();

// Export type for listener functions
export type { AssessmentUpdateListener };

